import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useAuth } from '../context/AuthContext';
import { getDirecciones, crearDireccion, actualizarDireccion, eliminarDireccion } from '../api/direcciones';
import CustomerForm from '../components/CustomerForm';
import useSEO from '../hooks/useSEO';
import logo from '../logo.jpg';
import './Home.css';
import './Carrito.css';
import './MisDirecciones.css';

const emptyCustomerData = {
  name: '',
  address: '',
  city: '',
  department: '',
  postalCode: '',
  phone: '',
  email: '',
  isValid: false,
};

const MisDirecciones = () => {
  useSEO({
    title: 'Mis Direcciones | MiEstampita',
    description: 'Administrá tus direcciones de envío guardadas en MiEstampita.',
    path: '/mis-direcciones',
    noindex: true,
  });

  const { isLoggedIn, user } = useAuth();
  const [direcciones, setDirecciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingPredeterminada, setEditingPredeterminada] = useState(false);
  const [alias, setAlias] = useState('');
  const [customerData, setCustomerData] = useState({ ...emptyCustomerData, email: user?.email || '' });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  const load = () => {
    setLoading(true);
    getDirecciones()
      .then(setDirecciones)
      .catch((err) => setError(err.message || 'No se pudieron cargar tus direcciones.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!isLoggedIn) {
      setLoading(false);
      return;
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  const cerrarFormulario = () => {
    setShowForm(false);
    setEditingId(null);
    setEditingPredeterminada(false);
    setAlias('');
    setCustomerData({ ...emptyCustomerData, email: user?.email || '' });
  };

  const handleEditar = (direccion) => {
    setEditingId(direccion.id);
    setEditingPredeterminada(Boolean(direccion.predeterminada));
    setAlias(direccion.alias);
    setCustomerData({
      name: direccion.nombre_completo,
      address: direccion.direccion,
      city: direccion.ciudad,
      department: direccion.departamento,
      postalCode: direccion.codigo_postal === 'S/N' ? '' : direccion.codigo_postal,
      phone: direccion.telefono,
      email: direccion.email,
      isValid: true,
    });
    setShowForm(true);
  };

  const handleGuardar = async (event) => {
    event.preventDefault();
    setSaveError('');
    setSaving(true);
    try {
      if (editingId) {
        await actualizarDireccion(editingId, {
          alias: alias.trim() || 'Envío',
          direccion: customerData,
          predeterminada: editingPredeterminada,
        });
      } else {
        await crearDireccion({ alias: alias.trim() || 'Envío', direccion: customerData });
      }
      cerrarFormulario();
      load();
    } catch (err) {
      setSaveError(err.message || 'No se pudo guardar la dirección.');
    } finally {
      setSaving(false);
    }
  };

  const handleEliminar = async (id) => {
    try {
      await eliminarDireccion(id);
      load();
    } catch (err) {
      setError(err.message || 'No se pudo eliminar la dirección.');
    }
  };

  if (!isLoggedIn) {
    return (
      <div className="home-page">
        <div className="logo-wrapper">
          <Link to="/">
            <img src={logo} alt="MiEstampita Logo" className="logo" />
          </Link>
        </div>
        <div className="home-wrapper">
          <p>Iniciá sesión para ver tus direcciones.</p>
          <Link to="/login" className="back-to-collection">
            Iniciar sesión
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="home-page">
      <div className="logo-wrapper">
        <Link to="/">
          <img src={logo} alt="MiEstampita Logo" className="logo" />
        </Link>
      </div>
      <div className="home-wrapper">
        <Link to="/coleccion" className="back-to-collection">
          ‹ Seguir comprando
        </Link>
        <h1 className="selected-title">Mis Direcciones</h1>

        {loading && <p>Cargando direcciones...</p>}
        {error && <p className="cart-cupon__error">{error}</p>}

        {!loading && direcciones.length === 0 && !showForm && <p>Todavía no guardaste ninguna dirección.</p>}

        {!loading && direcciones.length > 0 && (
          <ul className="direcciones-list">
            {direcciones.map((direccion) => (
              <li key={direccion.id} className="direccion-card">
                <div>
                  <strong>{direccion.alias}</strong>
                  {direccion.predeterminada ? <span className="direccion-card__badge">Predeterminada</span> : null}
                  <p>
                    {direccion.nombre_completo} — {direccion.direccion}, {direccion.ciudad}, {direccion.departamento}
                  </p>
                  <p className="direccion-card__contacto">
                    {direccion.telefono} · {direccion.email}
                  </p>
                </div>
                <div className="direccion-card__actions">
                  <button
                    type="button"
                    className="direccion-card__icon-button"
                    onClick={() => handleEditar(direccion)}
                    aria-label="Editar dirección"
                    title="Editar"
                  >
                    <EditOutlinedIcon fontSize="small" />
                  </button>
                  <button
                    type="button"
                    className="direccion-card__icon-button"
                    onClick={() => handleEliminar(direccion.id)}
                    aria-label="Eliminar dirección"
                    title="Eliminar"
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}

        {!loading && !showForm && (
          <button type="button" className="checkout-button" onClick={() => setShowForm(true)}>
            Agregar nueva dirección
          </button>
        )}

        {showForm && (
          // Sin <form> propio acá: CustomerForm ya renderiza el suyo, y
          // anidar <form> dentro de <form> es HTML inválido (React tira
          // error de hidratación). El guardado se dispara por click, no
          // por submit -- igual que en Carrito.jsx.
          <div className="direccion-form">
            <label htmlFor="alias">Alias (ej: Casa, Trabajo)</label>
            <input
              id="alias"
              type="text"
              value={alias}
              onChange={(event) => setAlias(event.target.value)}
              placeholder="Envío"
            />

            <CustomerForm customer={customerData} onChange={setCustomerData} />

            {saveError && <p className="cart-cupon__error">{saveError}</p>}

            <div className="checkout-button-wrapper">
              <button
                type="button"
                className="checkout-button"
                onClick={handleGuardar}
                disabled={saving || !customerData.isValid}
              >
                {saving ? 'Guardando...' : editingId ? 'Guardar cambios' : 'Guardar dirección'}
              </button>
              <button type="button" className="retry-link" onClick={cerrarFormulario}>
                Cancelar
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MisDirecciones;
